import { FormField, FormItem, FormMessage } from "@/ui/form";
import { SelectUsersAsync } from "@/features/users/components/SelectUsersAsync";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { useAuthenticated } from "@gettactic/hooks";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { UserChip } from "@cmp-src/data-display/chips/UserChip/UserChip";
import { toUserOption } from "@/features/users/utils/toUserOption";
import React, { useCallback, useEffect, useMemo } from "react";

type ReserveFieldUsersProps = {
  form: UseFormReturn<any>;
  defaultValue: string[];
  /** Hidden from the options and removed from the picked users, e.g. the organizer. */
  excludedUserId?: string | null;
  shouldOfferCurrentUser?: boolean;
  errors: FieldErrors<SaveReservationData>;
  single?: boolean;
  readonly?: boolean;
  onAfterChange?: () => void;
};
export function ReserveFieldUsers({
  form,
  defaultValue,
  excludedUserId,
  shouldOfferCurrentUser = false,
  single = false,
  readonly = false,
  onAfterChange
}: ReserveFieldUsersProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isTeamManager =
    !!authenticatedUser?.user?.permissions.includes(
      "schedule:others:managed:add"
    ) && !authenticatedUser?.user?.permissions.includes("schedule:others:add");

  const currentUser = authenticatedUser?.user;
  // Team managers' list only holds the users they manage, so the current user is added
  // here rather than relying on the list to include them.
  const currentUserOption = useMemo(
    () =>
      shouldOfferCurrentUser && currentUser
        ? toUserOption(currentUser)
        : undefined,
    [shouldOfferCurrentUser, currentUser]
  );

  const filterValues = useCallback(
    (option: { value: string }) => option.value !== excludedUserId,
    [excludedUserId]
  );

  // Who is excluded changes with the organizer, so a user picked earlier can become it.
  useEffect(() => {
    if (!excludedUserId) {
      return;
    }
    const users = (form.getValues("users") ?? []) as { value: string }[];
    if (users.some((user) => user.value === excludedUserId)) {
      form.setValue(
        "users",
        users.filter((user) => user.value !== excludedUserId),
        { shouldValidate: true }
      );
    }
  }, [excludedUserId, form]);

  const selectedUsers =
    (form.getValues("users") as
      | { label: string; value: string; profile_photo_url: string }[]
      | undefined) || [];

  return (
    <>
      <FormField
        control={form.control}
        name="users"
        render={({ field }) => (
          <FormItem>
            <SelectUsersAsync
              setInitial={(users) => {
                // Before we can set default values we need to do an async call to retrieve IDs.
                form.setValue("users", users, { shouldValidate: false });
              }}
              onChange={(value) => {
                field.onChange(single ? [value] : value);
                onAfterChange?.();
              }}
              readonly={readonly}
              value={field.value}
              className="w-full text-sm"
              classNames={{
                control: () => "mt-0! border-input! shadow-input",
                singleValue: () => "text-neutral-1000!",
                placeholder: () => "text-muted-foreground!"
              }}
              classNamePrefix="react-select"
              isMulti={!single}
              defaultValue={defaultValue}
              placeholder={
                single ? "Select User" : "Add guests by name or email"
              }
              filterValues={excludedUserId ? filterValues : undefined}
              currentUserOption={currentUserOption}
              managed={isTeamManager}
            />
            <FormMessage />
          </FormItem>
        )}
      />
      {!single && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedUsers.map((item) => {
            const onCancel = (event: React.MouseEvent) => {
              event.stopPropagation();
              event.preventDefault();
              form.setValue(
                "users",
                selectedUsers.filter((u) => u.value !== item.value),
                { shouldValidate: true }
              );
            };
            return (
              <UserChip
                key={item.value}
                user={{
                  name: item.label,
                  profile_photo_url: item.profile_photo_url
                }}
                onClick={onCancel}
                onCancel={onCancel}
                useColoredBorder
              />
            );
          })}
        </div>
      )}
    </>
  );
}
